import { Controller } from "@hotwired/stimulus";

/**
 * Plans Controller - Plan selection & state management

 * Responsibilities:
 * • Plan card selection & visual state (selected/unselected)
 * • Plan button text updates (Select/Change/Keep/Current)
 * • Change mode activation & premium auto-selection
 * • Enterprise vs basic/premium plan mode switching
 * • Inter-controller communication via plan-changed events
 * • Backend-driven category detection via data-category attributes

 * Replaces jQuery:
 * • Plan.selectPlan() - plan card clicking & selection
 * • Plan.change() / Plan.unchange() - change mode toggling
 * • Plan button state management & text updates
 */

export default class extends Controller {
  static DEFAULTS = {
    SELECTED_CLASS: "selected",
    PLAN_ACTION_SELECTED: "plan-action selected-plan",
    PLAN_ACTION_DEFAULT: "plan-action select-plan",
    YEAR: "year",
    MONTH: "month"
  };

  static targets = [
    "planCard",
    "planButton",
    "cardsWrapper",
    "monthlyButton",
    "yearlyButton",
    "continueButton",
    "addonSection",
    "addonContinueButton"
  ];

  static values = {
    selectedCategory: String,
    currentCategory: String,
    currentRecurrence: String,
    currentPlan: String,
    currentPlanLevel: Number,
    recurrence: String
  };

  connect() {
    // Initialize recurrence value
    this.recurrenceValue = this.currentRecurrenceValue || this.constructor.DEFAULTS.YEAR;

    // Set selected category from backend (if on a plan)
    if (this.currentCategoryValue) this.selectedCategoryValue = this.currentCategoryValue;

    this.updatePlanSelection();
    this.updateRecurrenceDisplay();
    this.handleChangeModeSelection();

    // Listen for any plan-changed events (including from pricing controller)
    this.element.addEventListener("plan-changed", this.handlePlanChanged);
  }

  disconnect() {
    this.element.removeEventListener("plan-changed", this.handlePlanChanged);
  }

  // Arrow function automatically binds 'this' context
  handlePlanChanged = (event) => {
    const { category, planName } = event.detail;
    if (category) {
      if (category !== this.selectedCategoryValue) {
        this.changePlanSelection(category);
        this.showContinueButton();
      } else {
        this.updateButtonTextForViewerChange(category);
      }
    }
  };

  selectPlan(event) {
    const card = event.currentTarget.closest("[data-category]");
    if (!card) return;

    this.changePlanSelection(card.dataset.category);
    this.showContinueButton();
  }

  getSelectedPlanType() {
    const category = this.selectedCategoryValue;
    const card = this.findPlanCardByCategory(category);

    if (!card) return null;

    // For plans with viewer dropdown (like premium), get the selected viewer count plan
    const capabilities = JSON.parse(card.dataset.capabilities || "{}");
    if (capabilities.hasSelectableViewerLimit) {
      const pricingController = this.getController("pricing");
      const viewerPlan = pricingController?.selectedPlanValue;
      if (viewerPlan) return viewerPlan;
    }

    // Otherwise return the card's plan name
    return card.dataset.plan;
  }

  // Centralized plan selection logic
  changePlanSelection(category) {
    if (category === this.selectedCategoryValue) return;

    this.selectedCategoryValue = category;
    this.updatePlanSelection();
    this.notifyPricingController();

    // If addon section is already visible, re-filter and reopen groups
    if (this.hasAddonSectionTarget && this.addonSectionTarget.style.display !== "none") {
      const pricingController = this.getController("pricing");
      if (pricingController) {
        pricingController.filterInlineAddonsByPlan();
        pricingController.refreshPricingForCategory(category);
      }
      this.openAddonGroups();
    }

  }

  activateChangeMode() {
    // Toggle change mode UI state
    const plansContainer = document.getElementById("plans");
    if (plansContainer) plansContainer.classList.add("is-changing");

    // Only auto-select premium if current category is not selectable
    const card = this.findPlanCardByCategory(this.selectedCategoryValue);
    const capabilities = card ? JSON.parse(card.dataset.capabilities || "{}") : {};

    if (capabilities.selectable) return;

    // Select the highest tier selectable plan (last in array, typically premium)
    const selectableCard = this.planCardTargets.findLast(c => JSON.parse(c.dataset.capabilities || "{}").selectable);
    if (selectableCard) this.changePlanSelection(selectableCard.dataset.category);
  }

  findPlanCardByCategory(category) {
    return this.planCardTargets.find(card => card.dataset.category === category);
  }

  updatePlanSelection() {
    this.planCardTargets.forEach(card => {
      const cardCategory = card.dataset.category;
      const isSelected = this.selectedCategoryValue === cardCategory;
      const capabilities = JSON.parse(card.dataset.capabilities || "{}");

      // Only apply selection styling to selectable plans
      if (capabilities.selectable) {
        card.classList.toggle(this.constructor.DEFAULTS.SELECTED_CLASS, isSelected);

        const button = card.querySelector("[data-plans-target='planButton']");
        if (button) {
          this.updateButtonState(button, isSelected);
        }
      }
    });

    // Update recurrence display based on selected plan
    this.updateRecurrenceDisplay();
  }

  // Toggles button text between default ("Subscribe") and selected ("Selected plan") states
  updateButtonState(button, isSelected) {
    const defaultSpan = button.querySelector("[data-plan-button-text='default']");
    const selectedSpan = button.querySelector("[data-plan-button-text='selected']");

    if (defaultSpan && selectedSpan) {
      if (isSelected) {
        defaultSpan.style.display = "none";
        selectedSpan.style.display = "inline";
        button.className = this.constructor.DEFAULTS.PLAN_ACTION_SELECTED;
      } else {
        defaultSpan.style.display = "inline";
        selectedSpan.style.display = "none";
        button.className = button.dataset.defaultClass || this.constructor.DEFAULTS.PLAN_ACTION_DEFAULT;
      }
    }
  }

  updateButtonTextForViewerChange(category) {
    const card = this.findPlanCardByCategory(category);
    if (!card) return;

    const capabilities = JSON.parse(card.dataset.capabilities || "{}");
    if (!capabilities.hasSelectableViewerLimit) return;

    const button = card.querySelector("[data-plans-target='planButton']");
    const selectedSpan = button.querySelector("[data-plan-button-text='selected']");
    if (!selectedSpan || !selectedSpan.dataset.keepText) return;

    const labelSpan = selectedSpan.querySelector("[data-plan-button-label]");
    if (!labelSpan) return;

    const viewerSelect = card.querySelector("[data-pricing-target='viewerSelect']");
    if (!viewerSelect) return;

    const selectedOption = viewerSelect.options[viewerSelect.selectedIndex];
    const selectedLevel = Number(selectedOption?.dataset.level);

    if (selectedLevel === this.currentPlanLevelValue) {
      labelSpan.textContent = selectedSpan.dataset.keepText;
    } else if (selectedLevel > this.currentPlanLevelValue) {
      labelSpan.textContent = selectedSpan.dataset.selectedText;
    } else {
      labelSpan.textContent = selectedSpan.dataset.downgradeText;
    }
  }

  handleChangeModeSelection() {
    const plansContainer = document.getElementById("plans");
    if (plansContainer && plansContainer.classList.contains("is-changing")) {
      this.activateChangeMode();
    }
  }

  updateRecurrenceDisplay() {
    const isMonthly = this.recurrenceValue === this.constructor.DEFAULTS.MONTH;
    const card = this.findPlanCardByCategory(this.selectedCategoryValue);
    const capabilities = card ? JSON.parse(card.dataset.capabilities || "{}") : {};
    const showToggle = capabilities.showMonthlyYearlyToggle;

    this.monthlyButtonTargets.forEach(btn => btn.classList.toggle("active", isMonthly && showToggle));
    this.yearlyButtonTargets.forEach(btn => btn.classList.toggle("active", !isMonthly && showToggle));
  }

  notifyPricingController() {
    const card = this.findPlanCardByCategory(this.selectedCategoryValue);
    if (!card) return;

    this.dispatch("plan-changed", { detail: { planName: card.dataset.plan, category: this.selectedCategoryValue }, bubbles: true });
  }

  getController(name) {
    const controllerKey = `_${name}Controller`;
    if (this[controllerKey]) return this[controllerKey];

    this[controllerKey] = this.application.getControllerForElementAndIdentifier(
      this.element,
      name
    );

    return this[controllerKey];
  }

  addonParamsForSelectedPlan() {
    const pricingController = this.getController("pricing");
    if (!pricingController || typeof pricingController.getAddonsForPlan !== "function") {
      return {features: {}, once: {}};
    }

    const addons = pricingController.getAddonsForPlan();
    const {recurring, once} = pricingController.splitRecurringAndOneTimeAddons(addons);

    return {features: recurring, once};
  }

  setRecurrence(event) {
    event.preventDefault();

    const period = event.currentTarget.dataset.period;
    if (![this.constructor.DEFAULTS.MONTH, this.constructor.DEFAULTS.YEAR].includes(period)) return;

    this.recurrenceValue = period;
    this.updateRecurrenceDisplay();

    // Notify pricing controller
    this.dispatch("recurrence-changed", {
      detail: { recurrence: period },
      bubbles: true
    });
  }

  showUnselectablePlans(event) {
    event?.preventDefault();
    event?.stopPropagation();

    this.hasCardsWrapperTarget && this.cardsWrapperTarget.classList.add("disabled-card-selected");

    const unselectableCard = this.planCardTargets.find(c =>
      !JSON.parse(c.dataset.capabilities || "{}").selectable
    );
    if (unselectableCard) {
      this.changePlanSelection(unselectableCard.dataset.category);
      this.showContinueButton();
    }
  }

  showSelectablePlans(event) {
    event?.preventDefault();

    this.hasCardsWrapperTarget && this.cardsWrapperTarget.classList.remove("disabled-card-selected");

    this.activateChangeMode();
    this.showContinueButton();
  }

  showAddonSection(event) {
    event?.preventDefault();

    if (!this.hasAddonSectionTarget) return;

    // Filter addons for the selected plan category and fetch fresh addon prices
    const pricingController = this.getController("pricing");
    if (pricingController) {
      pricingController.filterInlineAddonsByPlan();
      pricingController.refreshPricingForCategory(this.selectedCategoryValue);
    }

    this.addonSectionTarget.style.display = "";
    this.addonSectionTarget.scrollIntoView({ behavior: "smooth", block: "start" });

    // Track plan selection for new pricing plans
    if (window.shouldTrackNewPricing) {
      this.trackEvent("new_pricing_plan_selected");
    }

    // Hide this button — addon section handles everything from here
    if (this.hasContinueButtonTarget) {
      this.continueButtonTarget.style.display = "none";
    }
  }

  buildPlan(event) {
    event?.preventDefault();

    // Collapse addon groups to keep the view tidy while billing loads
    if (this.hasAddonSectionTarget) {
      this.addonSectionTarget.querySelectorAll(".closeable-group.group-opened").forEach(group => {
        group.querySelector(".closeable-group-header")?.click();
      });
    }

    const newPlanHolder = document.getElementById("new-plan");
    if (!newPlanHolder) return;

    const path = newPlanHolder.dataset.path;
    if (!path) return;

    const useExistingPlan = event?.params?.existingPlan === true;
    const params = this.buildPlanParams(useExistingPlan);

    this.toggleLoader(true);

    fetch(`${path}?${params.toString()}`, {headers: {"Accept": "text/html"}})
      .then(response => {
        if (!response.ok) throw new Error("Request failed");
        return response.text();
      })
      .then(html => {
        if (window.$) {
          window.$(newPlanHolder).html(html);
        } else {
          newPlanHolder.innerHTML = html;
        }

        const newPlanPayment = document.getElementById("new-plan-payment");
        if (newPlanPayment) newPlanPayment.innerHTML = "";

        const switchPayment = document.getElementById("switch-payment-provider");
        if (switchPayment) switchPayment.innerHTML = "";

        // Currency controller will automatically convert newly injected [data-base-amount] elements
        // when it receives the mutation or on next currency change
        // Manually trigger display update for newly injected content
        this.getController("currency")?.updateDisplay();

        newPlanHolder.scrollIntoView({behavior: "smooth", block: "start"});

        // After first billing load, change addon button to "Update"
        if (this.hasAddonContinueButtonTarget) {
          const updateText = this.addonContinueButtonTarget.dataset.updateText;
          if (updateText) this.addonContinueButtonTarget.textContent = updateText;
        }
      })
      .catch(() => {
        this.getController("payment")?.showError(
          I18n?.t?.("js.error_has_occurred") || "An error occurred"
        );
      })
      .finally(() => {
        this.toggleLoader(false);
      });
  }

  buildPlanParams(useExistingPlan = false) {
    const params = new URLSearchParams();
    const planType = useExistingPlan ? this.currentPlanValue : this.getSelectedPlanType();
    params.append("plan[plan_type_name]", planType);
    params.append("plan[recurrence]", this.recurrenceValue);

    const { features, once } = this.addonParamsForSelectedPlan();

    Object.entries(features).forEach(([key, quantity]) => {
      params.append(`plan[features][${key}]`, quantity);
    });

    Object.entries(once).forEach(([key, quantity]) => {
      params.append(`plan[once][${key}]`, quantity);
    });

    return params;
  }

  toggleLoader(show) {
    const loader = document.querySelector(".loader");
    if (!loader) return;

    loader.style.display = show ? "block" : "none";
  }

  trackEvent(eventType) {
    fetch("/admin/app_events", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").content
      },
      body: JSON.stringify({ event_type: eventType })
    }).catch(error => console.error("Tracking error:", error));
  }

  showContinueButton() {
    if (this.hasContinueButtonTarget) {
      // Hide button if no plan is selected or if enterprise/unselectable is selected
      const card = this.findPlanCardByCategory(this.selectedCategoryValue);
      const capabilities = card ? JSON.parse(card.dataset.capabilities || "{}") : {};
      const isSelectable = this.selectedCategoryValue && capabilities.selectable;
      const addonSectionVisible = this.hasAddonSectionTarget && this.addonSectionTarget.style.display !== "none";
      this.continueButtonTarget.style.display = isSelectable && !addonSectionVisible ? "" : "none";

      if (!isSelectable) {
        this.hideAddonAndBillingSections();
      }
    }
  }

  openAddonGroups() {
    if (!this.hasAddonSectionTarget) return;

    this.addonSectionTarget.querySelectorAll(".closeable-group.group-closed").forEach(group => {
      if (group.style.display !== "none") {
        group.querySelector(".closeable-group-header")?.click();
      }
    });
  }

  hideAddonAndBillingSections() {
    if (this.hasAddonSectionTarget) {
      this.addonSectionTarget.style.display = "none";
    }

    const newPlanHolder = document.getElementById("new-plan");
    if (newPlanHolder) newPlanHolder.innerHTML = "";

    const newPlanPayment = document.getElementById("new-plan-payment");
    if (newPlanPayment) newPlanPayment.innerHTML = "";
  }
}
